// src/App.jsx
import React, { useState, useContext, useMemo, useEffect, useRef, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, NavLink, Navigate } from "react-router-dom";
import {
  FaPrayingHands,
  FaBookReader,
  FaChartPie,
  FaListUl,
  FaMusic,
  FaTv,
  FaMicrophoneAlt,
  FaChalkboardTeacher,
  FaCalendarAlt,
  FaHome,
  FaPlayCircle,
  FaHeadphones,
  FaVideo,
  FaShoppingBag,
  FaStickyNote,
} from "react-icons/fa";

// Import Components
import HamburgerMenu from "./HamburgerMenu";
import Home from "./Home";
import Worship from "./Worship";
import WorshipRecord from "./WorshipRecord";
import Session from "./Session";
// import Studio from "./Studio";
import GameSlidesPage from "./GameSlidesPage";

// import BibleReader from "./BibleReader";
import BibleIsReader from "./BibleIsReader";
// import BibleReader2 from "./components/BibleReader2";
import Song from "./Song";
import Podcasts from "./components/Podcasts";
import Blog from "./components/Blog";
import Marketplace from "./components/Marketplace";
import Messages from "./components/Messages";
import BibleStudyTools from "./components/BibleStudyTools";
import Karaoke from "./components/Karaoke";
import Events from "./components/Events";
import SocialMedia from "./components/SocialMedia";
import Playlists from "./Playlists";
 

import LiveStreaming from "./components/LiveStreaming";
import SmallGroups from "./components/SmallGroups";
import ResourceLibrary from "./components/ResourceLibrary";
import Gamification from "./components/Gamification";
import Counseling from "./components/Counseling";
import Mentorship from "./components/Mentorship";
import Volunteer from "./components/Volunteer";
import Forums from "./components/Forums";
import PrayerWall from "./components/PrayerWall";
import Profile from "./components/Profile";
import Community from "./components/Community";
import Help from "./components/Help";
import Sounds from "./components/Sounds";
import Music from "./components/Music";
import UnderDevelopment from "./components/UnderDevelopment";
import Infographics from "./components/Infographics";
import Video from "./Video";
import Audio from "./Audio";
import DJPage from "./components/DJPage";
import VideoManager from "./VideoManager";
import Notes from "./Notes";

import YouTubeVocalRemover from "./components/VocalRemover";
import Store from "./components/Store";

import ChatBubble from "./components/ChatBubble";
import ScreenRecorderPanel from "./components/ScreenRecorderPanel";

// Import CSS
import "./App.css";
import ActiveSongContext from "./contexts/ActiveSongContext";
import SessionPresentationContext from "./contexts/SessionPresentationContext";
import PasswordGate from "./components/PasswordGate";
import { SHORTCUT_MAP } from "./navigationShortcuts";
// Use the favicon as the header logo

const DEFAULT_DOMAIN_PARAM = "gcc-catalog.blogspot.com";
const DOMAIN_EVENT = "gcc-domain-change";

const normalizeDomainParam = (value) => {
  const trimmed = (value || "").trim();
  if (!trimmed) {
    return DEFAULT_DOMAIN_PARAM;
  }
  const sanitized = trimmed.replace(/^https?:\/\//i, "").replace(/\/.*$/, "");
  if (/\.blogspot\.com$/i.test(sanitized)) {
    return sanitized.toLowerCase();
  }
  const slug = sanitized
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (!slug) {
    return DEFAULT_DOMAIN_PARAM;
  }
  return `${slug}.blogspot.com`;
};

const toShortDomainParam = (value) => {
  const normalized = normalizeDomainParam(value);
  return normalized.replace(/\.blogspot\.com$/i, "");
};
const DEFAULT_SHORT_DOMAIN = toShortDomainParam(DEFAULT_DOMAIN_PARAM);

const broadcastDomainEvent = (shortDomain) => {
  if (typeof window === "undefined") return;
  window.__GCC_DOMAIN__ = shortDomain;
  window.dispatchEvent(new CustomEvent(DOMAIN_EVENT, { detail: shortDomain }));
};


function Header() {
  const location = useLocation();
  const { activeSongTitle, activeCatalogTitle } = useContext(ActiveSongContext);
  const { isPresenting: headerIsPresenting, sessionTitle: headerSessionTitle } =
    useContext(SessionPresentationContext);
  const showSongTitle = (location.pathname === "/worship" || location.pathname === "/record") && activeSongTitle;
  const baseTitle = "Global Christian Community";
      const headerIconMap = useMemo(
      () => ({
        "/worship": FaPrayingHands,
        "/bible": FaBookReader,
        "/bibleis": FaBookReader,
        "/bible2": FaBookReader,
        "/video": FaPlayCircle,
        "/audio": FaHeadphones,
        // "/studio": FaYoutube,
        "/present": FaChartPie,
        "/session": FaListUl,
        "/songs": FaMusic,
        "/playlists": FaMusic,
        "/music": FaMusic,
        "/sounds": FaMusic,
        "/dj": FaMusic,
        "/live": FaTv,
        "/karaoke": FaMicrophoneAlt,
        "/messages": FaChalkboardTeacher,
        "/events": FaCalendarAlt,
        "/media-tools": FaVideo,
        "/store": FaShoppingBag,
        "/notes": FaStickyNote,
      }),
        []
      );
      const pageTitleMap = useMemo(
      () => ({
        "/worship": "Worship",
        "/bible": "Bible",
        "/bibleis": "Bible",
        "/bible2": "Bible",
        "/present": "Present",
        "/session": "Session",
        "/songs": "Songs",
        "/playlists": "Songs",
        "/music": "Music",
        "/sounds": "Sounds",
        "/dj": "DJ Mixer",
        "/live": "Stream",
        "/video": "Videos",
        "/audio": "Audio",
        "/karaoke": "Karaoke",
        "/messages": "Messages",
        "/events": "Events",
        "/media-tools": "Media Tools",
        "/store": "Store",
        "/notes": "Notes",
      }),
        []
      );
  const headerTitle = useMemo(() => {
    if (location.pathname === "/") return baseTitle;
    if (location.pathname === "/session" && headerIsPresenting && headerSessionTitle) {
      return headerSessionTitle;
    }
    if (location.pathname === "/worship" && activeCatalogTitle) {
      return `${pageTitleMap[location.pathname] || "Worship"} (${activeCatalogTitle})`;
    }
    return pageTitleMap[location.pathname] || baseTitle;
  }, [location.pathname, baseTitle, pageTitleMap, headerIsPresenting, headerSessionTitle, activeCatalogTitle]);
  const HeaderIcon = headerIconMap[location.pathname];

  useEffect(() => {
    const compactClass = "home-compact";
    if (location.pathname === "/") {
      document.body.classList.add(compactClass);
    } else {
      document.body.classList.remove(compactClass);
    }
    return () => document.body.classList.remove(compactClass);
  }, [location.pathname]);

  const isImmersiveMode = ["/worship", "/record"].includes(location.pathname); 
  const hideBaseTitle = isImmersiveMode && showSongTitle;

  return (
    <header className={`header ${location.pathname === "/" ? "home" : "other"}`}>
      <div className="logo" aria-live="polite">
        {!hideBaseTitle && (
          <>
          {HeaderIcon && location.pathname !== "/" && !headerIsPresenting && (
            <HeaderIcon className="header-icon" aria-hidden="true" />
          )}
            <span>{headerTitle}</span>
          </>
        )}
        {showSongTitle && <span className="active-song">{hideBaseTitle ? activeSongTitle : ` — ${activeSongTitle}`}</span>}
      </div>
      <img
        src={`${process.env.PUBLIC_URL}/favicon.ico`}
        alt="Logo"
        className="header-logo"
      />
    </header>
  );
}

const BOTTOM_NAV_ITEMS = [
  { to: "/", label: "Home", Icon: FaHome },
  { to: "/worship", label: "Worship", Icon: FaPrayingHands },
  { to: "/bibleis", label: "Bible", Icon: FaBookReader },
  { to: "/video", label: "Videos", Icon: FaPlayCircle },
  { to: "/audio", label: "Audio", Icon: FaHeadphones },
  { to: "/present", label: "Present", Icon: FaChartPie },
  { to: "/session", label: "Session", Icon: FaListUl },
  { to: "/live", label: "GCC TV", Icon: FaTv },
];

function BottomNavigation() {
  return (
    <nav className="bottom-nav" aria-label="Mobile navigation">
      {BOTTOM_NAV_ITEMS.map(({ to, label, Icon }) => (
        <NavLink
          key={to}
          to={to}
          aria-label={label}
          className={({ isActive }) => `bottom-nav-link${isActive ? " active" : ""}`}
        >
          <Icon />
        </NavLink>
      ))}
    </nav>
  );
}

function DomainParamSync({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const domainRef = useRef(DEFAULT_SHORT_DOMAIN);

  const applyDomainToUrl = useCallback(
    (shortDomain) => {
      const params = new URLSearchParams(location.search);
      let changed = false;
      if (params.get("domain") !== shortDomain) {
        params.set("domain", shortDomain);
        changed = true;
      }
      if (params.has("catalog")) {
        params.delete("catalog");
        changed = true;
      }
      if (!changed) return;
      const search = params.toString();
      const hash = location.hash || "";
      navigate(`${location.pathname}?${search}${hash}`, { replace: true });
    },
    [location.hash, location.pathname, location.search, navigate],
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let shortDomain = params.get("domain") || domainRef.current || DEFAULT_SHORT_DOMAIN;
    shortDomain = toShortDomainParam(normalizeDomainParam(shortDomain));
    if (domainRef.current !== shortDomain) {
      domainRef.current = shortDomain;
    }
    if (typeof window !== "undefined") {
      window.__GCC_DOMAIN__ = shortDomain;
    }
    window.dispatchEvent(new CustomEvent(DOMAIN_EVENT, { detail: shortDomain }));
    applyDomainToUrl(shortDomain);
  }, [applyDomainToUrl, location.search]);

  useEffect(() => {
    const handleDomainChange = (event) => {
      const requested = typeof event.detail === "string" ? event.detail : "";
      if (!requested) return;
      const normalized = toShortDomainParam(normalizeDomainParam(requested));
      if (domainRef.current === normalized) {
        applyDomainToUrl(normalized);
        return;
      }
      domainRef.current = normalized;
      applyDomainToUrl(normalized);
    };
    window.addEventListener(DOMAIN_EVENT, handleDomainChange);
  return () => window.removeEventListener(DOMAIN_EVENT, handleDomainChange);
  }, [applyDomainToUrl]);

  return children;
}

function NavigationShortcuts() {
  const navigate = useNavigate();
  const shortcutMap = SHORTCUT_MAP;

  useEffect(() => {
    const handleKeydown = (event) => {
      if (!(event.ctrlKey || event.metaKey) || !event.key) return;
      const key = event.key.toLowerCase();
      const target = shortcutMap[key];
      if (!target) return;
      const activeElement = document.activeElement;
      const activeTag = activeElement?.tagName?.toLowerCase();
      if (
        activeElement?.isContentEditable ||
        ["input", "textarea", "select"].includes(activeTag || "")
      ) {
        return;
      }

      event.preventDefault();
      if (target.external) {
        window.open(target.to, "_blank", "noopener,noreferrer");
        return;
      }
      navigate(target.to);
    };

    window.addEventListener("keydown", handleKeydown);
    return () => window.removeEventListener("keydown", handleKeydown);
  }, [navigate, shortcutMap]);

  return null;
}

function App() {
  const [activeSongTitle, setActiveSongTitle] = useState("");
  const [activeCatalogTitle, setActiveCatalogTitle] = useState("");
  const songContextValue = useMemo(
    () => ({ activeSongTitle, setActiveSongTitle, activeCatalogTitle, setActiveCatalogTitle }),
    [activeSongTitle, activeCatalogTitle]
  );
  const [sessionPresentation, setSessionPresentation] = useState({
    isPresenting: false,
    sessionTitle: "",
  });
  const sessionPresentationValue = useMemo(
    () => ({ ...sessionPresentation, setSessionPresentation }),
    [sessionPresentation]
  );

  return (
    <ActiveSongContext.Provider value={songContextValue}>
      <SessionPresentationContext.Provider value={sessionPresentationValue}>
        <Router>
          <DomainParamSync>
            {/* Hamburger Menu */}
            <HamburgerMenu />
            
            {/* Dynamic Header */}
            <Header />
            
            {/* Keyboard navigation shortcuts */}
            <NavigationShortcuts />
            
            {/* The route definitions */}
            <Routes>
              <Route path="/worship" element={<Worship />} />
              <Route path="/session" element={<Session />} />

              {/* <Route path="/bible" element={<BibleReader />} /> */}
              <Route path="/bibleis" element={<BibleIsReader />} />
              {/* <Route path="/bible2" element={<BibleReader2 />} /> */}
              <Route path="/songs" element={<Song />} />
              <Route path="/playlists" element={<Playlists />} />
              <Route path="/games" element={<GameSlidesPage />} />
              
              <Route path="/karaoke" element={<Karaoke />} />
              <Route path="/events" element={<Events />} />
              <Route path="/messages" element={<Messages />} />

              <Route path="/socialmedia" element={<SocialMedia />} />

              <Route path="/podcasts" element={<Podcasts />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/marketplace" element={<Marketplace />} />
              <Route path="/biblestudy" element={<BibleStudyTools />} />

              <Route path="/live" element={<LiveStreaming />} />
              <Route path="/smallgroups" element={<SmallGroups />} />
              <Route path="/resources" element={<ResourceLibrary />} />
              <Route path="/gamification" element={<Gamification />} />
              <Route path="/counseling" element={<Counseling />} />
              <Route path="/mentorship" element={<Mentorship />} />
              <Route path="/volunteer" element={<Volunteer />} />
              <Route path="/forums" element={<Forums />} />
              <Route path="/prayerwall" element={<PrayerWall />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/community" element={<Community />} />
              <Route path="/present" element={<Infographics />} />
              <Route path="/sounds" element={<Sounds />} />
              <Route path="/music" element={<Music />} />
              <Route path="/dj" element={<DJPage />} />
              <Route path="/store" element={<Store />} />
              <Route path="/vocal" element={<YouTubeVocalRemover />} />
              <Route path="/video" element={<Video />} />
              <Route path="/media-tools" element={<VideoManager />} />
              <Route path="/video-manager" element={<Navigate replace to="/media-tools" />} />
              <Route path="/audio" element={<Audio />} />
              <Route path="/notes" element={<Notes />} />

              <Route path="/help" element={<Help />} />
              {/* Placeholder for pages under development */}
              <Route path="/:pageName" element={<UnderDevelopment />} />
              <Route
                path="/record"
                element={
                  <PasswordGate>
                    <WorshipRecord />
                  </PasswordGate>
                }
              />
              {/* <Route path="/studio" element={<Studio />} /> */}


              
              <Route path="/" element={<Home />} />
              <Route path="*" element={<div className="not-found">Page Under Development, Please Visit Later</div>} />
            </Routes>
            <BottomNavigation />
            <ScreenRecorderPanel />
            {/* <ChatBubble /> ChatBubble appears on every page */}
          </DomainParamSync>
        </Router>
      </SessionPresentationContext.Provider>
    </ActiveSongContext.Provider>
  );
}

export default App;
